import React, { useEffect, useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import { PiArrowCircleLeftBold, PiArrowCircleRightBold } from "react-icons/pi";
import GeneralButton from "../SaveButton/GeneralButton";
import CrossButton from "../ButtonSpinner/CrossButton";
import CustomModal from "./CustomModal";
import { showSuccess, showError, showErrorMsg } from "../../../utils/showMessage";
import images from "../../../utils/images";
import { FetchGroupNames } from "../../../api/groups";
import { SendEmail } from "../../../api/report";
import { handleEmailKeyDown } from "../regex/Regex";
import { useSelector } from "react-redux";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { toast } from "react-toastify";
import { useReactSelectTheme } from "../../../utils/useReactSelectTheme";
import { UI_COLORS } from "../../../theme/colorConstants";
import { getProperReportName, formatReportType } from "../../dashboard/Reports/getReportsData";

const CustomReport = ({
  open,
  handleClose,
  getData,
  search,
  pageType,
  filterBy,
  advanceFilters,
  columns,id
}) => {
  const [reportName, setReportName] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedForMove, setSelectedForMove] = useState([]);
  const [selectedColumnValues, setSelectedColumnValues] = useState([]);
  const [exportType, setExportType] = useState(null);
  const [groupNames, setGroupNames] = useState([]);
  const [roleNames, setRoleNames] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [type, setType] = useState([]);
  const [validationError, setValidationError] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const capabilities = useSelector((state) => state.user?.user?.role?.capabilities || {});
  const isCapable = 'is_admin_user' in capabilities ? capabilities : false;
  const hasExportGroupsRolesDataCapability = isCapable?.report?.export_groups_roles_data;
  const { styles: selectStyles, theme: selectTheme } = useReactSelectTheme();

  const hideEmailForReports = ['epm_user_action_audit'];
  const shouldHideEmail = hideEmailForReports.includes(pageType);

  useEffect(() => {
    if (!open) return;

    const fetchDetails = async (type, setFunc) => {
      if (hasExportGroupsRolesDataCapability) {
        try {
          const { data } = await FetchGroupNames(type);
          setFunc(data ? data.map((item) => ({ label: item, value: item })) : []);
        } catch (error) {
          setFunc([]);
        }
      } else {
        setFunc([]);
      }
    };

    fetchDetails("group", setGroupNames);
    fetchDetails("role", setRoleNames);
  }, [open, hasExportGroupsRolesDataCapability]);

  useEffect(() => {
    if (open) {
      setReportName("");
      setReportDescription("");
      setSelectedColumns([]);
      setSelectedForMove([]);
      setExportType("");
      setIsChecked((prevState) => !prevState);
      setSelectedGroups([]);
      setSelectedRoles([]);
      setValidationError("");
    } else {
      setSelectedColumnValues([]);
      setSelectedForMove([]);
      setSelectedEmails([]);
    }
  }, [open]);

  useEffect(() => {
    if (selectedGroups.length && selectedRoles.length) {
      setType(["group", "role"]);
    } else if (selectedGroups.length) {
      setType(["group"]);
    } else if (selectedRoles.length) {
      setType(["role"]);
    } else {
      setType([]);
    }
  }, [selectedGroups, selectedRoles]);



  const handleKeyDownWrapper = (event) => {
    handleEmailKeyDown(
      event,
      inputValue,
      setInputValue,
      selectedEmails,
      setSelectedEmails,
      setValidationError,
      createOption
    );
  };

  const moveSelectedToSelectedColumns = () => {
    const updatedColumns = selectedForMove.filter((col) =>
      columns.includes(col)
    );
    setSelectedColumns([...selectedColumns, ...updatedColumns]);
    setSelectedColumnValues([
      ...selectedColumnValues,
      ...updatedColumns.map((col) => col.value),
    ]);
    setSelectedForMove([]);
  };

  const moveSelectedToColumnList = () => {
    const updatedColumns = selectedColumns.filter((col) =>
      selectedForMove.includes(col)
    );
    setSelectedColumns(
      selectedColumns?.filter((col) => !selectedForMove.includes(col))
    );
    setSelectedColumnValues(
      selectedColumnValues?.filter(
        (value) => !updatedColumns.map((col) => col.value).includes(value)
      )
    );
    setSelectedForMove([]);
  };

  const handleSelectForMove = (column) => {
    setSelectedForMove(
      selectedForMove.includes(column)
        ? selectedForMove?.filter((col) => col !== column)
        : [...selectedForMove, column]
    );
  };

  const handleSubmit = async () => {
    if (exportType === "email") {
      if (shouldHideEmail) {
        setValidationError("Email option is not available for this report type.");
        setLoading(false);
        return;
      }

      if (
        !selectedEmails.length &&
        !selectedGroups.length &&
        !selectedRoles.length
      ) {
        setValidationError(
          "Please specify at least one email or select either group or role."
        );
        return;
      }
    }

    setLoading(true);
    try {
      if (exportType === "email") {
        const emailValues = selectedEmails.map((email) => email.value);
        
        const {data,error} = await SendEmail(
          emailValues,
          "csv",
          pageType,
          advanceFilters,
          search,
          filterBy,
          selectedGroups,
          selectedRoles,
          type,
          selectedColumnValues,
          reportName,
          reportDescription,id
        );
        if(data){
        showSuccess(data.message);}
      } else {
        // CSV export - getData function already handles the toast
        await getData(
          null,
          null,
          search,
          pageType,
          "csv",
          filterBy,
          advanceFilters,
          selectedColumnValues
        );
      }
    } catch (error) {
      showErrorMsg(error," Failed to Export Reports");
    } finally {
      setLoading(false);
      setSelectedGroups([]);
      setSelectedRoles([]);
      setSelectedEmails([]);
      setSelectedColumns([]);
      setSelectedColumnValues([]);
      setSelectedForMove([]);
      setValidationError("");
    }
  };

  // Merge theme styles with custom disabled background and menu maxHeight
  const customSelectStyles = {
    ...selectStyles,
    control: (styles, state) => ({
      ...selectStyles.control(styles, state),
      backgroundColor: state.isDisabled ? UI_COLORS.disabled.background : undefined,
      paddingTop: "6px",
      paddingBottom: "6px",
    }),
    menu: (provided) => ({
      ...selectStyles.menu(provided),
      maxHeight: "200px",
      overflowY: "auto",
    }),
    menuList: (styles) => ({
      ...selectStyles.menuList(styles),
      maxHeight: "200px",
      overflowY: "auto",
    }),
  };

  const handleSelectChange = (newValue) => setSelectedEmails(newValue);
  const createOption = (label) => ({ label, value: label });
  const isButtonDisabled = exportType === "email"
  ? (
      selectedEmails.length === 0 &&
      selectedGroups.length === 0 &&
      selectedRoles.length === 0
    ) || selectedColumnValues.length === 0
  : selectedColumnValues.length === 0;

  const components = {
    DropdownIndicator: null,
  };

  return (
    <CustomModal
      open={open}
      handleClose={handleClose}
      title="Create Custom Report"
      maxHeight="90%"
      width="55%"
    >
      <div>
        <Container
          fluid
          className="mx-auto add_systemuser_container"
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="main_content_heading">
              Create Custom CSV Report - {formatReportType(pageType)}
            </h2>
            <CrossButton onClick={handleClose} />
          </div>

          <>
            <Form.Group as={Row} className="mb-3 justify-content-between">
              <Form.Label column md={3} className="input_label">
                Report Name
              </Form.Label>
              <Col md={9}>
                <Form.Control
                  className="form_input_field"
                  type="text"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  placeholder="Enter Report Name"
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3 justify-content-between">
              <Form.Label column md="3" className="input_label">
                Report Description
              </Form.Label>
              <Col md="9">
                <Form.Control
                  as="textarea"
                  rows={3}
                  className="form_input_field"
                  value={reportDescription}
                  placeholder="Enter Report Description"
                  onChange={(e) => setReportDescription(e.target.value)}
                />
              </Col>
            </Form.Group>

            <Row className="align-items-center">
              <Col md={5}>
                <Form.Label className="input_label">Columns List</Form.Label>
                <div
                  style={{
                    borderWidth: "1px",
                    borderStyle: "ridge",
                    borderColor: UI_COLORS.borderAdditional.lightGray,
                    padding: "10px",
                    height: "200px",
                    overflowY: "scroll",
                  }}
                >
                  {columns?.filter((col) => !selectedColumns.includes(col))
                    .map((column, index) => (
                      <p
                        className="form_input_field"
                        key={index}
                        style={{
                          cursor: "pointer",
                          backgroundColor: selectedForMove.includes(column)
                            ? UI_COLORS.chart.primaryOverlay
                            : "transparent",
                          margin: 1,
                          padding: "5px 0",
                        }}
                        onClick={() => handleSelectForMove(column)}
                        onDoubleClick={() => {
                          setSelectedColumns([...selectedColumns, column]);
                          setSelectedForMove((prev) =>
                            prev?.filter((col) => col !== column)
                          );
                        }}
                      >
                        {column.label}
                      </p>
                    ))}
                </div>
              </Col>

              <Col
                md={2}
                className="d-flex flex-column align-items-center"
                style={{ marginTop: "0px" }}
              >
                <div
                  style={{
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  onClick={moveSelectedToSelectedColumns}
                >
                  <PiArrowCircleRightBold size={30} style={{ color: "grey" }} />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  onClick={moveSelectedToColumnList}
                >
                  <PiArrowCircleLeftBold size={30} style={{ color: "grey" }} />
                </div>
              </Col>

              <Col md={5}>
                <Form.Label className="input_label">
                  Selected Columns
                </Form.Label>
                <div
                  className="form_input_field"
                  style={{
                    borderWidth: "1px",
                    borderStyle: "ridge",
                    borderColor: UI_COLORS.borderAdditional.lightGray,
                    padding: "10px",
                    height: "200px",
                    overflowY: "scroll",
                  }}
                >
                  {selectedColumns.map((column, index) => (
                    <p
                      key={index}
                      style={{
                        cursor: "pointer",
                        backgroundColor: selectedForMove.includes(column)
                          ? UI_COLORS.chart.primaryOverlay
                          : "transparent",
                        margin: 1,
                        padding: "5px 0",
                      }}
                      onClick={() => handleSelectForMove(column)}
                      onDoubleClick={() => {
                        setSelectedColumns((prev) =>
                          prev?.filter((col) => col !== column)
                        );
                      }}
                    >
                      {column.label}
                    </p>
                  ))}
                </div>
              </Col>
            </Row>

            {!shouldHideEmail && (
              <Row className="pt-3 mb-3 justify-content-between align-items-center">
                <Col sm="auto" className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    style={{ fontSize: "1rem", cursor: "pointer" }}
                    id="custom-checkbox"
                    label={
                      <span style={{ cursor: "pointer" }}>Email this Report</span>
                    }
                    className="form_input_field"
                    onChange={(e) => {
                      setExportType(e.target.checked ? "email" : null); 
                      if (!e.target.checked) {
                        setSelectedEmails([]); 
                      }
                    }}
                  />
                </Col>

                {exportType === "email" && (
                <div>
                  <Row className="pt-3">
                    <Col md={9}>
                      <Form.Label className="input_label">
                  Select Groups or Roles or manually enter emails for
                        sending reports
                </Form.Label>
                    </Col>
                  </Row>
                  <Row className="pt-3">
                    <Col md={2} className="d-flex align-items-center">
                     <Form.Label className="input_label">
                        Groups
                      </Form.Label>
                      
                    </Col>
                    <Col md={7}>
                      <Select
                        options={groupNames}
                        className="p-0 form_input_field"
                        isMulti
                        isDisabled={!hasExportGroupsRolesDataCapability}
                        placeholder={hasExportGroupsRolesDataCapability ? "Select Groups" : "Groups (No Access)"}
                        value={selectedGroups.map((group) => ({
                          label: group,
                          value: group,
                        }))}
                        onChange={(selected) =>
                          setSelectedGroups(
                            selected.map((group) => group.value)
                          )
                        }
                        styles={customSelectStyles}
                        theme={selectTheme}
                      />
                    </Col>
                  </Row>

                  <Row className="pt-3">
                    <Col md={2} className="d-flex align-items-center">
                     <Form.Label className="input_label">
                        Roles
                      </Form.Label>
                    </Col>
                    <Col md={7}>
                      <Select
                        className="p-0 form_input_field"
                        isMulti
                        isDisabled={!hasExportGroupsRolesDataCapability}
                        placeholder={hasExportGroupsRolesDataCapability ? "Select Roles" : "Roles (No Access)"}
                        value={selectedRoles.map((role) => ({
                          label: role,
                          value: role,
                        }))}
                        onChange={(selected) =>
                          setSelectedRoles(selected.map((role) => role.value))
                        }
                        options={roleNames}
                        styles={customSelectStyles}
                        theme={selectTheme}
                      />
                    </Col>
                  </Row>

                  <Row className="pt-3">
                    <Col md={2} className="d-flex align-items-center">
                     <Form.Label className="input_label">
                        Specify Emails
                      </Form.Label>
                    </Col>
                    <Col md={7}>
                      <CreatableSelect
                        className="form_input_field"
                        isMulti
                        isClearable
                        components={components}
                        inputValue={inputValue}
                        menuIsOpen={false}
                        placeholder="Enter the emails and press enter"
                        onChange={handleSelectChange}
                        onInputChange={(newValue, actionMeta) => {
                          if (
                            actionMeta.action !== "input-blur" &&
                            actionMeta.action !== "menu-close"
                          ) {
                            setInputValue(newValue);
                            if (!newValue.trim()) {
                              setValidationError("");
                            }
                          }
                        }}
                        onKeyDown={handleKeyDownWrapper}
                        onBlur={() => {
                          if (inputValue && inputValue.trim()) {
                            handleKeyDownWrapper({
                              key: "Enter",
                              preventDefault: () => {},
                            });
                          }
                        }}
                        value={selectedEmails}
                        styles={customSelectStyles}
                        theme={selectTheme}
                      />
                    </Col>
                  </Row>
                </div>
                )}
              </Row>
            )}

            {validationError && (
              <div
                className="error-message"
                style={{ color: "red", marginTop: "10px" }}
              >
                {validationError}
              </div>
            )}

            <Row className="pt-3 mb-3 justify-content-end">
              <Col sm="auto">
                <GeneralButton
                  onClickEvent={handleClose}
                  className="me-1"
                  value="Cancel"
                  color={UI_COLORS.text.gray}
                  variant="outlined"
                  size="large"
                />
              </Col>
              <Col sm="auto" className="me-sm-2 p-0">
                {loading ? (
                  <GeneralButton
                    variant="contained"
                    disabled={true}
                    className="me-1"
                    value={
                      <LazyLoadImage
                        src={images.Loader}
                        width="26px"
                        height="26px"
                        alt="loading"
                      />
                    }
                    size="large"
                  />
                ) : (
                  <GeneralButton
                    className="me-1"
                    onClickEvent={handleSubmit}
                    value="Generate Report"
                    variant="contained"
                    size="large"
                    disabled={isButtonDisabled}
                  />
                )}
              </Col>
            </Row>
          </>
        </Container>
      </div>
    </CustomModal>
  );
};

export default CustomReport;
